import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import {
    Card,
    CardContent,
    CardHeader,
    Chip,
    Container,
    Divider,
    FormControl,
    Grid,
    IconButton,
    InputLabel,
    LinearProgress,
    MenuItem,
    Paper,
    Select,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableFooter,
    TableHead,
    TablePagination,
    TableRow,
    TextField,
    Typography,
} from "@mui/material";
import { Box } from "@mui/system";
import { DateTime } from "luxon";
import React from "react";
import Fetch from "../api/Fetch";
import { EMS_BACKEND_URI } from "../constants";
import AuthStore from "../stores/AuthStore";
import ClearIcon from "@mui/icons-material/Close";
import FilterListIcon from "@mui/icons-material/FilterList";
import EventIcon from "@mui/icons-material/Event";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import WorkIcon from "@mui/icons-material/Work";
import PersonIcon from "@mui/icons-material/Person";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import ScheduleStore from "../stores/ScheduleStore";
import { Event } from "../model/Types";

const Schedule = () => {
    const [events, setEvents] = React.useState<any[]>([]);
    const [page, setPage] = React.useState(0);
    const [rowsPerPage, setRowsPerPage] = React.useState(10);

    const [fromDate, setFromDate] = React.useState<string>(new Date().toISOString());
    const [toDate, setToDate] = React.useState<string>(new Date().toISOString());
    const [locationId, setLocationId] = React.useState<number | undefined>(undefined);
    const [positionId, setPositionId] = React.useState<number | undefined>(undefined);

    const [loading, setLoading] = React.useState<boolean>(false);

    const fromDateOnChange = (date?: any) => {
        if (date) {
            setFromDate(date.toString());
        }
    };

    const toDateOnChange = (date?: any) => {
        if (date) {
            setToDate(date.toString());
        }
    };

    const refreshEvents = async () => {
        setLoading(true);
        try {
            const body = await Fetch<Event[]>(`${EMS_BACKEND_URI}/punch-clock/event`, {
                includeAuth: true,
            });
            setEvents(body);
        } catch (err) {
            console.log(err);
        } finally {
            setTimeout(() => {
                setLoading(false);
            }, 500);
        }
    };

    const eventOnChange = async (e: any) => {
        if (e.target.value > 0) {
            setPage(0);
            setLoading(true);
            try {
                await ScheduleStore.eventOnChange(e.target.value);
                // setFromDate(new Date(Date.parse(ScheduleStore.event?.startDate)).toISOString());
                // setToDate(new Date(Date.parse(ScheduleStore.event?.endDate)).toISOString());
            } catch (err) {
                console.log(err);
            } finally {
                setTimeout(() => {
                    setLoading(false);
                }, 500);
            }
        }
    };

    React.useEffect(() => {
        refreshEvents();
    }, []);

    const { event, eventId } = ScheduleStore;

    return (
        <Container maxWidth="xl" sx={{ py: 3 }}>
            <Typography variant="h4" sx={{ mb: 3, fontWeight: 500 }}>
                Schedule
            </Typography>
            <Paper elevation={2} sx={{ mb: 3, borderRadius: 2 }}>
                <Box sx={{ p: 2, backgroundColor: "action.selected", borderRadius: "8px 8px 0 0" }}>
                    <Typography variant="h6" sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <FilterListIcon /> Filters
                    </Typography>
                </Box>
                <CardContent sx={{ pt: 3 }}>
                    <Grid container spacing={3}>
                        <Grid item xs={12} md={6}>
                            <FormControl fullWidth variant="outlined">
                                <InputLabel id="events-label-id">Event</InputLabel>
                                <Select
                                    label="Event"
                                    labelId="events-label-id"
                                    value={eventId}
                                    onChange={eventOnChange}
                                    disabled={loading}
                                    startAdornment={<EventIcon sx={{ ml: 1, mr: 0.5, color: "action.active" }} />}
                                >
                                    {events
                                        .sort((a, b) => a.name.localeCompare(b.name))
                                        .map((it) => (
                                            <MenuItem key={it.id} value={it.id}>
                                                {it.name}
                                            </MenuItem>
                                        ))}
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                            <FormControl fullWidth variant="outlined">
                                <InputLabel id="position-label-id">Position</InputLabel>
                                <Select
                                    label="Position"
                                    labelId="position-label-id"
                                    value={positionId}
                                    onChange={(e: any) => setPositionId(e.target.value)}
                                    startAdornment={<WorkIcon sx={{ ml: 1, mr: 0.5, color: "action.active" }} />}
                                >
                                    <MenuItem value={undefined}>All Positions</MenuItem>
                                    {event?.positions
                                        ?.sort((a, b) => a.name.localeCompare(b.name))
                                        .map((it: any) => (
                                            <MenuItem key={it.id} value={it.id}>
                                                {it.name}
                                            </MenuItem>
                                        ))}
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                            <FormControl fullWidth variant="outlined">
                                <InputLabel id="location-label-id">Location</InputLabel>
                                <Select
                                    label="Location"
                                    labelId="location-label-id"
                                    value={locationId}
                                    onChange={(e: any) => setLocationId(e.target.value)}
                                    startAdornment={<LocationOnIcon sx={{ ml: 1, mr: 0.5, color: "action.active" }} />}
                                >
                                    <MenuItem value={undefined}>All Locations</MenuItem>
                                    {event?.locations
                                        ?.sort((a, b) => a.name.localeCompare(b.name))
                                        .map((it: any) => (
                                            <MenuItem key={it.id} value={it.id}>
                                                {it.name}
                                            </MenuItem>
                                        ))}
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={6}>
                            <DatePicker
                                label="From Date"
                                onChange={fromDateOnChange}
                                renderInput={(params) => <TextField {...params} fullWidth size="small" />}
                                value={fromDate}
                            />
                        </Grid>
                        <Grid item xs={6}>
                            <DatePicker
                                label="To Date"
                                onChange={toDateOnChange}
                                renderInput={(params) => <TextField {...params} fullWidth size="small" />}
                                value={toDate}
                            />
                        </Grid>
                    </Grid>
                </CardContent>
            </Paper>
            <Paper elevation={2} sx={{ borderRadius: 2 }}>
                <TableContainer>
                    <Table>
                        <TableHead>
                            <TableRow sx={{ backgroundColor: "primary.light" }}>
                                <TableCell sx={{ fontWeight: 600 }}>Shift / Spot</TableCell>
                                <TableCell sx={{ fontWeight: 600 }}>Position</TableCell>
                                <TableCell sx={{ fontWeight: 600 }}>Location</TableCell>
                                <TableCell sx={{ fontWeight: 600 }}>Date</TableCell>
                                <TableCell sx={{ fontWeight: 600 }}>Time</TableCell>
                                <TableCell sx={{ fontWeight: 600 }}>Assignee</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {loading ? (
                                <TableRow>
                                    <TableCell colSpan={6} sx={{ textAlign: "center", py: 4 }}>
                                        <LinearProgress />
                                    </TableCell>
                                </TableRow>
                            ) : (event?.shifts?.length ?? 0) === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={6} sx={{ textAlign: "center", py: 6 }}>
                                        <Typography variant="h6" color="text.secondary">
                                            No schedule data available
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                                            Select an event to view the schedule
                                        </Typography>
                                    </TableCell>
                                </TableRow>
                            ) : (
                                event?.shifts
                                    .filter((it: any) => !locationId || it.location.id === locationId)
                                    .filter((it: any) => !positionId || it.position.id === positionId)
                                    .map((shift: any) =>
                                        shift.recurrences
                                            .filter(
                                                (it: any) =>
                                                    DateTime.fromISO(it.date).startOf("day") >= DateTime.fromISO(fromDate).startOf("day") &&
                                                    DateTime.fromISO(it.date).endOf("day") <= DateTime.fromISO(toDate).endOf("day")
                                            )
                                            .map((recurrence: any) => {
                                                return recurrence.spots
                                                    .map((spot: any) => {
                                                        return {
                                                            shiftSpotId: spot.id,
                                                            shiftId: shift.id,
                                                            locationName: shift.location.name,
                                                            positionName: shift.position.name,
                                                            date: recurrence.date,
                                                            beginTime: DateTime.fromISO(shift.beginTime).toFormat("t"),
                                                            endTime: DateTime.fromISO(shift.endTime).toFormat("t"),
                                                            notes: shift.notes,
                                                            shiftSpot: spot,
                                                        };
                                                    })
                                                    .flat();
                                            })
                                            .flat()
                                    )
                                    .flat()
                                    .sort((a: any, b: any) =>
                                        a.shiftId > b.shiftId || (a.shiftId === b.shiftId && a.shiftSpotId > b.shiftSpotId) ? 1 : -1
                                    )
                                    .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                                    .map((it: any, index: number) => (
                                        <TableRow
                                            key={`${it.shiftId}-${it.shiftSpotId}`}
                                            sx={{
                                                "&:hover": { backgroundColor: "action.hover" },
                                                backgroundColor: index % 2 === 0 ? "background.paper" : "action.disabledBackground",
                                            }}
                                        >
                                            <TableCell>
                                                <Chip label={`${it.shiftId} / ${it.shiftSpotId}`} size="small" variant="outlined" />
                                            </TableCell>
                                            <TableCell>
                                                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                                                    <WorkIcon fontSize="small" color="action" />
                                                    {it.positionName}
                                                </Box>
                                            </TableCell>
                                            <TableCell>
                                                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                                                    <LocationOnIcon fontSize="small" color="action" />
                                                    {it.locationName}
                                                </Box>
                                            </TableCell>
                                            <TableCell>{DateTime.fromISO(it.date).toFormat("MMM dd, yyyy")}</TableCell>
                                            <TableCell>
                                                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                                                    <AccessTimeIcon fontSize="small" color="action" />
                                                    {it.beginTime} - {it.endTime}
                                                </Box>
                                            </TableCell>
                                            <TableCell>
                                                {it.shiftSpot.fulfilledBy ? (
                                                    <Chip
                                                        icon={<PersonIcon />}
                                                        label={`${it.shiftSpot.fulfilledBy.firstName} ${it.shiftSpot.fulfilledBy.lastName}`}
                                                        color="primary"
                                                        size="small"
                                                        variant="filled"
                                                    />
                                                ) : (
                                                    <Typography variant="body2" color="text.secondary" sx={{ fontStyle: "italic" }}>
                                                        Unassigned
                                                    </Typography>
                                                )}
                                            </TableCell>
                                        </TableRow>
                                    ))
                            )}
                        </TableBody>
                        <TableFooter>
                            <TablePagination
                                count={
                                    event?.shifts
                                        .filter((it: any) => !locationId || it.location.id === locationId)
                                        .filter((it: any) => !positionId || it.position.id === positionId)
                                        .map((shift: any) =>
                                            shift.recurrences
                                                .filter(
                                                    (it: any) =>
                                                        DateTime.fromISO(it.date).startOf("day") >= DateTime.fromISO(fromDate).startOf("day") &&
                                                        DateTime.fromISO(it.date).endOf("day") <= DateTime.fromISO(toDate).endOf("day")
                                                )
                                                .map((recurrence: any) => {
                                                    return recurrence.spots
                                                        .map((spot: any) => {
                                                            return {
                                                                shiftSpotId: spot.id,
                                                                shiftId: shift.id,
                                                                locationName: shift.location.name,
                                                                positionName: shift.position.name,
                                                                date: recurrence.date,
                                                                beginTime: DateTime.fromISO(shift.beginTime).toFormat("t"),
                                                                endTime: DateTime.fromISO(shift.endTime).toFormat("t"),
                                                                notes: shift.notes,
                                                                shiftSpot: spot,
                                                            };
                                                        })
                                                        .flat();
                                                })
                                                .flat()
                                        )
                                        .flat().length ?? 0
                                }
                                onPageChange={(e, v) => setPage(v)}
                                page={page}
                                onRowsPerPageChange={(e) => {
                                    setRowsPerPage(parseInt(e.target.value, 10));
                                    setPage(0);
                                }}
                                rowsPerPage={rowsPerPage}
                            />
                        </TableFooter>
                    </Table>
                </TableContainer>
            </Paper>
        </Container>
    );
};

export default Schedule;
